import { SKIP_RENDER } from '@mediashop/app/constants/semanticConstants';
import Variants from '@mediashop/app/domain/Variants';
import useIsPreview from '@mediashop/app/hooks/useIsPreview';
import { usePartner } from '@mediashop/app/hooks/usePartner';
import { useProduct } from '@mediashop/app/hooks/useProduct';
import Bundle from '@mediashop/base/pattern/organism/ProductStage/Bundle';
import VariantSelector from '@mediashop/base/pattern/organism/ProductStage/VariantSelector';
import { useEffect, useMemo, useRef, useState } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import { useLoader } from '@mediashop/app/store/loader';
import { Variant } from '@mediashop/app/api/types/ClientProduct';
import getInstallmentPrice from '@mediashop/catalog-base/utils/getInstallmentPrice';
import { TrackableButton } from '@mediashop/app/analytics/components/TrackableButton';
import { TrackingKeys } from '@mediashop/app/analytics/constants/trackingKeys';
import StickyAddToCartBanner from '../StickyAddToCartBanner/StickyAddToCartBanner';
import { ProductDetailsContentProps } from './index';
import PurchaseDetails from './PurchaseDetails';
import ProductDetailsPrice from './ProductDetailsPrice';
import { useCheaperSetPrice } from '@mediashop/app/hooks/api/useCheaperSetPrice';
import parse from 'html-react-parser';
import useToast from '@mediashop/app/hooks/useToast';
import { useLocation } from 'react-router';
import AboSelector from '../../AboSelector';
import { SubscriptionInterval } from '../../AboSelector/AboSelector';
import { DetailedVariant } from '@mediashop/app/api/types/ClientProductDetailed';
import { getProductSlug } from '@mediashop/app/helper/getProductSlug';
import useEventTracking from '@mediashop/app/hooks/useEventTracking';
import { useBrxPageContext } from '@mediashop/app/bloomreach/useBrxPageContext';
import { useNavigate } from '@mediashop/app/hooks/useNavigate';

const componentName = 'product-details-content';

// eslint-disable-next-line max-lines-per-function, complexity
function ProductDetailsContent({
    product: productEntity,
    activeVariant,
    detailBadges,
    setVariant,
    shippingCostsInfo,
    productRelationType,
    infoTextForPaymentByInstallments,
    showSubscriptionProduct,
    onSubscriptionChanged,
    hasSaleBadge,
    isVariantSelected,
    variantImage,
    hideAddToCartBar,
}: ProductDetailsContentProps): JSX.Element {
    const isPreview = useIsPreview();
    const navigate = useNavigate();
    const { search, pathname } = useLocation();
    const brxPage = useBrxPageContext();
    const { trackPDPViewed } = useEventTracking();

    const { product, status: productStatus } = productEntity || {};

    const { cartLoader } = useLoader();
    const intl = useIntl();
    const toast = useToast();

    const ref = useRef<HTMLHeadingElement>(null);

    const { activeRelatedProduct: activeProductRelation } = useProduct();

    const [expanded, setExpanded] = useState(false);
    const [isInProgress, setIsInProgress] = useState(false);
    const [isError, setIsError] = useState(false);
    const [showDeliveryModal, setShowDeliveryModal] = useState(false);
    const [showInstallmentModal, setShowInstallmentModal] = useState<boolean>(false);
    const [addToCartErrorMessage, setAddToCartErrorMessage] = useState<string>();
    const [subscriptionInterval, setSubscriptionInterval] = useState<SubscriptionInterval | undefined>(undefined);
    const [availableVariants, setAvailableVariants] = useState<Variant[]>([]);
    const productHasMultipleVariants = product?.variants?.length && product.variants?.length > 1;

    const [shouldTrackPdpViewedEvent, setShouldTrackPdpViewedEvent] = useState(!productHasMultipleVariants);

    const trackPDPViewedEvent = (variant: Variant) => {
        if (shouldTrackPdpViewedEvent) {
            const sku = variant.sku;
            const page = brxPage.toJSON().page;
            const componentCount = Object.keys(page).filter((prop) => page[prop].type === 'container-item').length;
            trackPDPViewed(sku, componentCount);
        }
    };

    useEffect(() => {
        if (
            product?.subscriptionProduct &&
            product?.subscriptionProduct.variants.length > 1 &&
            productStatus === 'succeeded'
        ) {
            const subscriptionVariants = Variants.getApplicableVariants(product.subscriptionProduct.variants);
            if (subscriptionVariants.length > 1) {
                setAvailableVariants([...Variants.getApplicableVariants(product.variants), ...subscriptionVariants]);
            }
        } else if (product && productStatus === 'succeeded') {
            setAvailableVariants(Variants.getApplicableVariants(product.variants));
        } else {
            setAvailableVariants([]);
        }

        const productHasAboVariantSelector =
            (activeVariant as DetailedVariant)?.attributes.subscriptionBaseVariantSku ||
            (activeVariant as DetailedVariant)?.attributes.subscriptionVariantSku;
        setShouldTrackPdpViewedEvent(Boolean(productHasMultipleVariants) || productHasAboVariantSelector);
    }, [product, productStatus]);

    useEffect(() => {
        setSubscriptionInterval(
            activeVariant.attributes.subscriptionDefaultIntervall
                ? JSON.parse(activeVariant.attributes.subscriptionDefaultIntervall)
                : undefined
        );
    }, [activeVariant]);

    useEffect(() => {
        trackPDPViewedEvent(activeVariant);
    }, []);

    const showStickyBar =
        !hideAddToCartBar && (!productHasMultipleVariants || (productHasMultipleVariants && isVariantSelected));

    const disableProduct = useMemo(
        () => !activeVariant?.price?.centAmount || activeVariant?.price?.centAmount === 0,
        [activeVariant]
    );

    const isInstallmentTextAvailable = Boolean(
        typeof infoTextForPaymentByInstallments === 'object' && infoTextForPaymentByInstallments?.value
    );

    const { cheaperSetPrice } = useCheaperSetPrice({ variant: activeVariant });

    const productNumberText = activeVariant?.sku
        ? `${intl.formatMessage({ id: 'product.productNumber' })}: ${activeVariant?.sku}`
        : '';

    const priceClass = detailBadges.length ? `price-colored-${detailBadges[0]?.badgeProps?.style}` : ``;

    const priceForInstallmentPrice = isVariantSelected ? activeVariant?.price : activeVariant?.cheapestPrice;
    const installmentPrice = activeVariant
        ? getInstallmentPrice(priceForInstallmentPrice ?? activeVariant?.price)
        : undefined;
    const maxUspCount = 4;
    const [hidePreCheckoutFlyout, setHidePreCheckoutFlyout] = useState(true);

    /**
     * Get PartnerId from Redux
     */
    const { partnerId } = usePartner();
    /**
     * Add article to cart and redirect to cart.
     */
    const handleAddToCartClick = async (quantity: number) => {
        setIsInProgress(true);
        setHidePreCheckoutFlyout(true);
        try {
            if (!isVariantSelected && product && product?.variants?.length > 1) {
                setIsError(true);
            } else if (activeVariant) {
                setHidePreCheckoutFlyout(false);
                setAddToCartErrorMessage(undefined);
                const { exceededMaxQuantity } = await cartLoader.addLineItem({
                    partner: partnerId,
                    quantity,
                    relation:
                        activeProductRelation?.relationType && activeProductRelation?.relatedProductSku
                            ? {
                                  mainProductSku: activeProductRelation.relatedProductSku,
                                  relationType: activeProductRelation.relationType,
                              }
                            : undefined,
                    subscriptionInterval,
                    variant: activeVariant,
                });
                if (exceededMaxQuantity) {
                    const errorMessage = intl.formatMessage(
                        { id: 'product.exceededMaxOrderQuantity' },
                        { amount: `${activeVariant.attributes?.maxOrderQuantity}` }
                    );
                    toast.error(errorMessage);
                    setAddToCartErrorMessage(errorMessage);
                }
            } else {
                setIsError(true);
            }
        } catch {
            const addToCartErrorMessage = intl.formatMessage({ id: 'product.cantAddToCart' });
            toast.error(addToCartErrorMessage);
            setAddToCartErrorMessage(addToCartErrorMessage);
        } finally {
            setIsInProgress(false);
        }
    };

    const setUrlPath = (selectedVariant: Variant): void => {
        const slug = getProductSlug(pathname) ?? '';

        let productSlug: string = '';
        if (product?.variants.find((variant) => variant.sku === selectedVariant.sku)) {
            productSlug = product.slug;
        } else if (product?.subscriptionProduct?.variants.find((variant) => variant.sku === selectedVariant.sku)) {
            productSlug = product.subscriptionProduct.slug;
        }

        const newPathname = pathname.replace(slug, productSlug);
        const searchParams = new URLSearchParams(search);
        searchParams.set('variant', selectedVariant.sku);

        if (newPathname) {
            navigate(
                { pathname: newPathname, search: searchParams.toString() },
                { replace: true, preventScrollReset: true, state: 'preventScroll' }
            );
        }
    };

    /**
     * Select a variant and reset error
     *
     * @param variant
     */
    const selectVariant = (variant?: Variant) => {
        if (variant) {
            trackPDPViewedEvent(variant);
            setIsError(false);
            setVariant(variant);

            if (!isPreview) {
                setUrlPath(variant);
            }
        } else {
            setVariant(null);
        }
    };

    const onInternalSubscriptionChanged = (variant: DetailedVariant, subscriptionInterval: SubscriptionInterval) => {
        selectVariant(variant);
        setSubscriptionInterval(subscriptionInterval);
        if (onSubscriptionChanged) {
            onSubscriptionChanged(Boolean(variant.attributes.subscriptionBaseVariantSku));
        }
    };

    const onIntervalChanged = (interval: SubscriptionInterval) => {
        setSubscriptionInterval(interval);
    };
    const shippingTime = activeVariant?.attributes?.shippingTime;
    const markGreen = shippingTime?.split(':');
    const currentUspCount = activeVariant?.attributes?.usp?.length;
    const isOversize = maxUspCount < currentUspCount;
    const nrOfUspsToShow = expanded ? currentUspCount : maxUspCount;

    const showMoreButtonRef = useRef<HTMLButtonElement>(null);
    const handleExpandDescription = () => {
        setExpanded(!expanded);

        setTimeout(() => {
            if (showMoreButtonRef.current) {
                showMoreButtonRef.current.scrollIntoView({
                    behavior: 'smooth',
                    block: 'center',
                });
            }
        }, 100);
    };

    return (
        <div className={`${componentName}__wrapper`}>
            <div className={`${componentName}__product-number`}>
                {product && product?.variants?.length > 1 ? isVariantSelected && productNumberText : productNumberText}
            </div>
            <div className={`${componentName}__description-wrapper`}>
                <div className={`${componentName}__description`} ref={ref}>
                    {activeVariant?.attributes?.usp && (
                        <ul className={`${componentName}__description-list`}>
                            {activeVariant?.attributes?.usp.slice(0, nrOfUspsToShow).map((item, key) => (
                                <li className={`${componentName}__description-item`} key={key}>
                                    <h3>{parse(item)}</h3>
                                </li>
                            ))}
                        </ul>
                    )}
                </div>
                {isOversize && (
                    <TrackableButton
                        ref={showMoreButtonRef}
                        className={`${componentName}__show-more-btn`}
                        onClick={handleExpandDescription}
                        trackingKey={TrackingKeys.PRODUCT_DETAILS_TOGGLE}
                    >
                        <FormattedMessage id={expanded ? 'product.detailsShowLess' : 'product.detailsShowMore'} />
                    </TrackableButton>
                )}
            </div>
            {availableVariants.length > 1 ? (
                <div className={`${componentName}__variant-switcher`}>
                    <VariantSelector
                        isError={isError}
                        onVariantChange={(variant) => selectVariant(variant)}
                        selectedVariant={isVariantSelected ? activeVariant : undefined}
                        totalNumberOfVariants={product?.totalNumberOfVariants ?? 0}
                        variants={availableVariants}
                        showSubscriptionVariants={showSubscriptionProduct}
                    />
                </div>
            ) : (
                SKIP_RENDER
            )}
            <AboSelector
                product={product}
                selectedDefaultVariant={isVariantSelected ? (activeVariant as DetailedVariant) : undefined}
                onSubscriptionSelected={onInternalSubscriptionChanged}
                onIntervalSelected={onIntervalChanged}
            />
            {!disableProduct ? (
                <>
                    <ProductDetailsPrice
                        activeVariant={showSubscriptionProduct ? activeVariant : activeVariant}
                        componentName={componentName}
                        detailBadges={detailBadges}
                        hasSaleBadge={hasSaleBadge}
                        isInstallmentTextAvailable={isInstallmentTextAvailable}
                        isVariantSelected={isVariantSelected}
                        priceClass={priceClass}
                        setShowInstallmentModal={setShowInstallmentModal}
                        showInstallmentModal={showInstallmentModal}
                        variant={activeVariant}
                        infoTextForPaymentByInstallments={infoTextForPaymentByInstallments}
                        installmentPrice={installmentPrice}
                        product={product}
                        productHasMultipleVariants={productHasMultipleVariants}
                    />
                    <PurchaseDetails
                        cheaperSetPrice={cheaperSetPrice}
                        componentName={componentName}
                        dontShowPreCheckoutFlyout={hidePreCheckoutFlyout}
                        handleAddToCartClick={handleAddToCartClick}
                        isInProgress={isInProgress}
                        markGreen={markGreen}
                        product={product}
                        productRelationType={productRelationType}
                        shippingCostsInfo={shippingCostsInfo}
                        setShowDeliveryModal={setShowDeliveryModal}
                        showDeliveryModal={showDeliveryModal}
                        variant={activeVariant}
                        addToCartErrorMessage={addToCartErrorMessage}
                    />
                </>
            ) : (
                <div className={`${componentName}__product-unavailable`}>
                    <div className={`${componentName}__product-unavailable-headline`}>
                        <div className={`${componentName}__product-unavailable-indicator`} />
                        <FormattedMessage id="product.productUnavailable.headline" />
                    </div>
                    <FormattedMessage id="product.productUnavailable.text" />
                </div>
            )}

            {product?.sets?.length ? <Bundle sets={product.sets} /> : SKIP_RENDER}
            {showStickyBar && (
                <StickyAddToCartBanner
                    addToCartErrorMessage={addToCartErrorMessage}
                    dontShowPreCheckoutFlyout={hidePreCheckoutFlyout}
                    handleAddToCartClick={handleAddToCartClick}
                    isInProgress={isInProgress}
                    product={product}
                    productRelationType={productRelationType}
                    variant={activeVariant}
                    variantImage={variantImage}
                />
            )}
        </div>
    );
}

export default ProductDetailsContent;
